import { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Box from 'elements/Box';
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Chip,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import SaveIcon from '@mui/icons-material/Save';
import LockIcon from '@mui/icons-material/Lock';

import { ApiRequest } from 'GlobalFunctions';
import AlertDialog from 'elements/AlertDialog';

const monthNames = [
  '', 'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const statusLabels: any = {
  created: 'Creado',
  in_progress: 'En progreso',
  closed: 'Finalizado',
};

const statusColors: any = {
  created: 'default',
  in_progress: 'primary',
  closed: 'success',
};

/*=========================================================
CURRENCY INPUT HELPERS
- Store integers internally
- Display with thousand separators ($1.000.000)
=========================================================*/
const formatCurrencyInput = (value: number): string => {
  if (!value && value !== 0) return '';
  return value.toLocaleString('es-CO');
};

const parseCurrencyInput = (raw: string): number => {
  // Remove everything that is not a digit
  const digits = raw.replace(/[^\d]/g, '');
  return parseInt(digits, 10) || 0;
};

export default function BudgetDetail(props: any) {
  const { setLoading } = props;
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { budget_id } = useParams();

  const [budget, setBudget] = useState<any>(null);
  const [businessDays, setBusinessDays] = useState(0);
  const [workedDays, setWorkedDays] = useState(0);
  const [users, setUsers] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);
  const [openCloseAlert, setOpenCloseAlert] = useState(false);

  const isClosed = budget?.status === 'closed';

  useEffect(() => {
    if (budget_id) {
      loadDetails();
    }
  }, [budget_id]);

  const loadDetails = async () => {
    setLoading(true);

    await ApiRequest({
      url: "/budgets/details",
      method: "get",
      headers: {
        ltkn: localStorage.getItem("ltkn"),
      },
      query: {
        budget_id: budget_id,
      },
      setResponse: (response: any) => {
        if (response.status === 'success') {
          setBudget(response.data);
          setBusinessDays(response.data.business_days);
          setWorkedDays(response.data.worked_days);
          setUsers(response.data.users || []);
        } else {
          enqueueSnackbar('Error al cargar el presupuesto', { variant: 'error' });
        }
        setLoading(false);
      },
    });
  };

  /*=========================================================
  UPDATE USER FIELD (integers only)
  =========================================================*/
  const handleUserChange = (index: number, field: string, rawValue: string) => {
    const updated = [...users];
    updated[index] = {
      ...updated[index],
      [field]: parseCurrencyInput(rawValue),
    };
    setUsers(updated);
  };

  /*=========================================================
  COMPUTED VALUES PER ROW
  =========================================================*/
  const computedRows = useMemo(() => {
    return users.map((user) => {
      const budgetAmount = parseInt(user.budget_amount) || 0;
      const actualSales = parseInt(user.actual_sales) || 0;

      const compliance = budgetAmount > 0
        ? ((actualSales / budgetAmount) * 100)
        : 0;

      let projection = 0;
      if (workedDays > 0) {
        projection = ((actualSales / workedDays) * (businessDays - workedDays)) + actualSales;
      }

      const projectionPercent = budgetAmount > 0
        ? ((projection / budgetAmount) * 100)
        : 0;

      return {
        ...user,
        compliance,
        projection: Math.round(projection),
        projectionPercent,
      };
    });
  }, [users, businessDays, workedDays]);

  /*=========================================================
  TOTALS
  =========================================================*/
  const totals = useMemo(() => {
    const totalBudget = computedRows.reduce((sum, r) => sum + (parseInt(r.budget_amount) || 0), 0);
    const totalActualSales = computedRows.reduce((sum, r) => sum + (parseInt(r.actual_sales) || 0), 0);
    const totalProjection = computedRows.reduce((sum, r) => sum + r.projection, 0);

    const totalCompliance = totalBudget > 0
      ? ((totalActualSales / totalBudget) * 100)
      : 0;

    const totalProjectionPercent = totalBudget > 0
      ? ((totalProjection / totalBudget) * 100)
      : 0;

    return {
      budget: totalBudget,
      actualSales: totalActualSales,
      compliance: totalCompliance,
      projection: totalProjection,
      projectionPercent: totalProjectionPercent,
    };
  }, [computedRows]);

  /*=========================================================
  BULK SAVE (auto sets status to in_progress)
  =========================================================*/
  const handleSave = async () => {
    setSaving(true);

    const body = new FormData();
    body.append('formValues', JSON.stringify({
      budget_id: budget_id,
      business_days: businessDays,
      worked_days: workedDays,
      users: users.map((u) => ({
        budget_user_id: u.budget_user_id,
        budget_amount: parseInt(u.budget_amount) || 0,
        actual_sales: parseInt(u.actual_sales) || 0,
      })),
    }));

    await ApiRequest({
      url: "/budgets/bulkSave",
      method: "post",
      headers: {
        ltkn: localStorage.getItem("ltkn"),
      },
      body: body,
      setResponse: (response: any) => {
        if (response.status === 'success') {
          enqueueSnackbar('Presupuesto guardado exitosamente', { variant: 'success' });
          loadDetails(); // Reload to reflect new status
        } else {
          enqueueSnackbar(response.message || 'Error al guardar', { variant: 'error' });
        }
        setSaving(false);
      },
    });
  };

  /*=========================================================
  CLOSE BUDGET
  =========================================================*/
  const handleCloseBudget = async () => {
    setSaving(true);

    const body = new FormData();
    body.append('formValues', JSON.stringify({
      budget_id: budget_id,
      status: 'closed',
    }));

    await ApiRequest({
      url: "/budgets/update",
      method: "post",
      headers: {
        ltkn: localStorage.getItem("ltkn"),
      },
      body: body,
      setResponse: (response: any) => {
        if (response.status === 'success') {
          enqueueSnackbar('Presupuesto finalizado', { variant: 'success' });
          loadDetails(); // Reload to reflect closed status
        } else {
          enqueueSnackbar(response.message || 'Error al cerrar', { variant: 'error' });
        }
        setSaving(false);
      },
    });
  };

  /*=========================================================
  FORMAT HELPERS
  =========================================================*/
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
    }).format(value);
  };

  const formatPercent = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  if (!budget) return null;

  return (
    <div id='BudgetDetail' className='ModuleContainer'>
      <div className="ModuleWrapper">

        {/* HEADER */}
        <div className="boxContainerHeader">
          <div className="boxContainerTitle" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <ArrowBackIosNewIcon
              fontSize="small"
              onClick={() => navigate('/budgets')}
              sx={{ cursor: 'pointer', color: 'action.active' }}
            />
            <div>
              <h2 className="h2_title" style={{ margin: 0 }}>
                {monthNames[budget.month]} {budget.year}
              </h2>
              <span className="h2_subtitle">Presupuesto mensual</span>
            </div>
            <Chip
              label={statusLabels[budget.status] || budget.status}
              color={statusColors[budget.status] || 'default'}
              variant="outlined"
              size="small"
            />
            {!isClosed && (
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<LockIcon />}
                onClick={() => setOpenCloseAlert(true)}
                disabled={saving}
              >
                FINALIZAR MES
              </Button>
            )}
          </div>

          <div className="boxContainerActions" style={{ display: 'flex', gap: '8px' }}>
            {!isClosed && (
              <>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'Guardando...' : 'Guardar Todo'}
                </Button>
              </>
            )}
          </div>
        </div>

        {/* BUDGET HEADER FIELDS */}
        <Box className='box100'>
          <div style={{ display: 'flex', gap: '24px', padding: '16px', alignItems: 'center' }}>
            <TextField
              label="Días Laborables"
              type="number"
              value={businessDays}
              onChange={(e) => setBusinessDays(Number(e.target.value))}
              inputProps={{ min: 0, max: 31 }}
              size="small"
              sx={{ width: 160 }}
              disabled={isClosed}
            />
            <TextField
              label="Días Trabajados"
              type="number"
              value={workedDays}
              onChange={(e) => setWorkedDays(Number(e.target.value))}
              inputProps={{ min: 0, max: 31 }}
              size="small"
              sx={{ width: 160 }}
              disabled={isClosed}
            />
          </div>
        </Box>

        {/* USERS TABLE */}
        <Box className='box100'>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: '#a8a8a8', borderBottom: 'none' }}>ZONA</TableCell>
                  <TableCell sx={{ color: '#a8a8a8', borderBottom: 'none' }}>VENDEDOR</TableCell>
                  <TableCell sx={{ color: '#a8a8a8', borderBottom: 'none' }}>PRESUPUESTO</TableCell>
                  <TableCell sx={{ color: '#a8a8a8', borderBottom: 'none' }}>DÍAS LAB.</TableCell>
                  <TableCell sx={{ color: '#a8a8a8', borderBottom: 'none' }}>REALIZADO</TableCell>
                  <TableCell sx={{ color: '#a8a8a8', borderBottom: 'none' }}>% CUMPLIMIENTO</TableCell>
                  <TableCell sx={{ color: '#a8a8a8', borderBottom: 'none' }}>DÍAS TRAB.</TableCell>
                  <TableCell sx={{ color: '#a8a8a8', borderBottom: 'none' }}>PROYECCIÓN</TableCell>
                  <TableCell sx={{ color: '#a8a8a8', borderBottom: 'none' }}>% PROYECCIÓN</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {computedRows.map((row: any, index: number) => (
                  <TableRow key={row.budget_user_id} hover>
                    <TableCell>
                      <span className='flex_title'>{row.zone_name || 'Sin zona'}</span>
                    </TableCell>
                    <TableCell>
                      <span className='flex_title'>
                        {row.user_name} {row.user_last_name}
                      </span>
                    </TableCell>
                    <TableCell>
                      {isClosed ? (
                        <span className='flex_title'>{formatCurrency(parseInt(users[index].budget_amount) || 0)}</span>
                      ) : (
                        <TextField
                          value={formatCurrencyInput(parseInt(users[index].budget_amount) || 0)}
                          onChange={(e) => handleUserChange(index, 'budget_amount', e.target.value)}
                          size="small"
                          variant="outlined"
                          inputProps={{ style: { textAlign: 'right' } }}
                          InputProps={{ startAdornment: <span style={{ color: '#999', marginRight: 2 }}>$</span> }}
                          sx={{ width: 150 }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <span className='flex_title'>{businessDays}</span>
                    </TableCell>
                    <TableCell>
                      {isClosed ? (
                        <span className='flex_title'>{formatCurrency(parseInt(users[index].actual_sales) || 0)}</span>
                      ) : (
                        <TextField
                          value={formatCurrencyInput(parseInt(users[index].actual_sales) || 0)}
                          onChange={(e) => handleUserChange(index, 'actual_sales', e.target.value)}
                          size="small"
                          variant="outlined"
                          inputProps={{ style: { textAlign: 'right' } }}
                          InputProps={{ startAdornment: <span style={{ color: '#999', marginRight: 2 }}>$</span> }}
                          sx={{ width: 150 }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={formatPercent(row.compliance)}
                        size="small"
                        sx={{
                          backgroundColor: row.compliance >= 100 ? '#e8f5e9' : row.compliance >= 70 ? '#fff3e0' : '#ffebee',
                          color: row.compliance >= 100 ? '#2e7d32' : row.compliance >= 70 ? '#e65100' : '#c62828',
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <span className='flex_title'>{workedDays}</span>
                    </TableCell>
                    <TableCell>
                      <span className='flex_title'>{formatCurrency(row.projection)}</span>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={formatPercent(row.projectionPercent)}
                        size="small"
                        sx={{
                          backgroundColor: row.projectionPercent >= 100 ? '#e8f5e9' : row.projectionPercent >= 70 ? '#fff3e0' : '#ffebee',
                          color: row.projectionPercent >= 100 ? '#2e7d32' : row.projectionPercent >= 70 ? '#e65100' : '#c62828',
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}

                {/* TOTALES */}
                {computedRows.length > 0 && (
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell>
                      <span className='flex_title' style={{ fontWeight: 700 }}>TOTALES</span>
                    </TableCell>
                    <TableCell></TableCell>
                    <TableCell>
                      <span className='flex_title' style={{ fontWeight: 700 }}>{formatCurrency(totals.budget)}</span>
                    </TableCell>
                    <TableCell>
                      <span className='flex_title' style={{ fontWeight: 700 }}>{businessDays}</span>
                    </TableCell>
                    <TableCell>
                      <span className='flex_title' style={{ fontWeight: 700 }}>{formatCurrency(totals.actualSales)}</span>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={formatPercent(totals.compliance)}
                        size="small"
                        sx={{
                          backgroundColor: totals.compliance >= 100 ? '#e8f5e9' : totals.compliance >= 70 ? '#fff3e0' : '#ffebee',
                          color: totals.compliance >= 100 ? '#2e7d32' : totals.compliance >= 70 ? '#e65100' : '#c62828',
                          fontWeight: 700,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <span className='flex_title' style={{ fontWeight: 700 }}>{workedDays}</span>
                    </TableCell>
                    <TableCell>
                      <span className='flex_title' style={{ fontWeight: 700 }}>{formatCurrency(totals.projection)}</span>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={formatPercent(totals.projectionPercent)}
                        size="small"
                        sx={{
                          backgroundColor: totals.projectionPercent >= 100 ? '#e8f5e9' : totals.projectionPercent >= 70 ? '#fff3e0' : '#ffebee',
                          color: totals.projectionPercent >= 100 ? '#2e7d32' : totals.projectionPercent >= 70 ? '#e65100' : '#c62828',
                          fontWeight: 700,
                        }}
                      />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </div>

      {/* ALERT PARA CERRAR PRESUPUESTO */}
      <AlertDialog
        open={openCloseAlert}
        setOpen={setOpenCloseAlert}
        subtitle="Al finalizar este presupuesto no podrá volver a editar los datos. ¿Está seguro que desea finalizar?"
        send={handleCloseBudget}
      />
    </div>
  );
}
